import React, { useEffect, useState } from "react";
import { BiFile, BiFolder, BiChevronRight, BiChevronDown, BiDownload } from "react-icons/bi";
import "./styles/Documents.css";

const Documents = () => {
    const [fileTree, setFileTree] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const buildFileTreeFromPublic = async () => {
        try {
            // Fetch the manifest file that lists all available files and folders
            const manifestResponse = await fetch("/familytreedata/manifest.json");
            if (manifestResponse.ok) {
                const manifest = await manifestResponse.json();
                return manifest;
            }
            throw new Error("Could not load manifest.json");
        } catch (err) {
            console.error("Error loading file tree from manifest:", err);
            throw new Error("Unable to load documents. Please ensure manifest.json exists in /public/familytreedata/");
        }
    };

    useEffect(() => {
        const buildFileTree = async () => {
            try {
                const tree = await buildFileTreeFromPublic();
                setFileTree(tree);
                setLoading(false);
            } catch (err) {
                setError(`Failed to load documents: ${err.message}`);
                setLoading(false);
            }
        };

        buildFileTree();
    }, []);

    const toggleFolder = (path) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFolders(newExpanded);
    };

    const handleDownload = (filePath, fileName) => {
        const link = document.createElement("a");
        link.href = filePath;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderFileTree = (node, level = 0) => {
        if (!node) return null;

        if (node.type === "file") {
            return (
                <div key={node.path} className="file-item">
                    <div className="file-content">
                        <BiFile className="file-icon" />
                        <span className="file-name">{node.name}</span>
                    </div>
                    <button className="download-btn" onClick={() => handleDownload(node.path, node.name)} title={`Download ${node.name}`}>
                        <BiDownload /> Download
                    </button>
                </div>
            );
        }

        if (node.type === "folder") {
            const isExpanded = expandedFolders.has(node.path);
            const hasChildren = node.children && node.children.length > 0;
            const chevronIcon = isExpanded ? <BiChevronDown /> : <BiChevronRight />;
            const toggleContent = hasChildren ? chevronIcon : <span style={{ width: "1em" }} />;

            return (
                <div key={node.path} className="folder-item">
                    <div className="folder-header" onClick={() => toggleFolder(node.path)} style={{ paddingLeft: `${level * 20}px` }}>
                        <span className="folder-toggle">{toggleContent}</span>
                        <BiFolder className="folder-icon" />
                        <span className="folder-name">{node.name}</span>
                        {node.children && <span className="file-count">({node.children.length})</span>}
                    </div>
                    {isExpanded && hasChildren && <div className="folder-contents">{node.children.map((child) => renderFileTree(child, level + 1))}</div>}
                </div>
            );
        }

        return null;
    };

    const homeStyle = {
        margin: "1em 5% 0 5%",
    };

    const headerStyle = {
        textAlign: "center",
        marginBottom: "1.5em",
    };

    const contentStyle = {
        backgroundColor: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "0.5em",
        padding: "1.5em",
        minHeight: "400px",
    };

    // Apply night mode styling if needed
    const isDarkMode = document.body.classList.contains("night-mode");
    if (isDarkMode) {
        contentStyle.backgroundColor = "#2d2d2d";
        contentStyle.border = "1px solid #444";
        contentStyle.color = "#e0e0e0";
    }

    if (loading) {
        return (
            <div style={homeStyle}>
                <div style={headerStyle}>
                    <h3>Documents</h3>
                </div>
                <div style={contentStyle}>
                    <p>Loading documents...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={homeStyle}>
                <div style={headerStyle}>
                    <h3>Documents</h3>
                </div>
                <div style={contentStyle}>
                    <p style={{ color: "red" }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={homeStyle}>
            <div style={headerStyle}>
                <h3>Documents</h3>
                <p>Download family tree documents and related files</p>
            </div>
            <div style={contentStyle}>{fileTree ? renderFileTree(fileTree) : <p>No documents available</p>}</div>
        </div>
    );
};

export default Documents;
